import { z } from 'zod';

import { type FieldFormatId, matchesFormat } from './formats';
import {
  DATADOG_SITES,
  DEFAULT_DATADOG_SITE,
  type ProviderId,
} from './schemas';

/**
 * One credential input. `kind:'secret'` is encrypted at rest (Infisical KMS);
 * `kind:'setting'` is non-secret and stored in plaintext status (e.g. a Datadog
 * site). `format` references a pure validator by id so the descriptor stays
 * serialisable.
 */
export interface CredentialField {
  readonly key: string;
  readonly kind: 'secret' | 'setting';
  readonly inputType: 'password' | 'text' | 'select';
  readonly required: boolean;
  readonly maxLength: number;
  readonly defaultValue?: string;
  readonly options?: readonly string[];
  readonly format?: FieldFormatId;
  /** i18n leaf under `home.integrations.<id>.<labelKey>`. */
  readonly labelKey: string;
  readonly placeholderKey: string;
}

/** A client-safe, serialisable description of a connectable provider. */
export interface ProviderDescriptor {
  readonly id: ProviderId;
  /** Where the user creates the credential. */
  readonly helpUrl: string;
  readonly fields: readonly CredentialField[];
  /** Capability hints shown in the form (e.g. `['read']`). */
  readonly recommendedCapabilities?: readonly string[];
  /** Whether `GET …/credentials` returns a meaningful "it works" list, so the
   * panel only renders the proof button when there is something to show. */
  readonly hasProof: boolean;
}

export const PROVIDERS: Record<ProviderId, ProviderDescriptor> = {
  github: {
    id: 'github',
    helpUrl: 'https://github.com/settings/tokens',
    recommendedCapabilities: ['repo', 'read:org', 'read:user'],
    hasProof: true,
    fields: [
      {
        key: 'token',
        kind: 'secret',
        inputType: 'password',
        required: true,
        maxLength: 255,
        format: 'github_classic_pat',
        labelKey: 'fields.token.label',
        placeholderKey: 'fields.token.placeholder',
      },
    ],
  },
  linear: {
    id: 'linear',
    helpUrl: 'https://linear.app/settings/api',
    recommendedCapabilities: ['read'],
    hasProof: true,
    fields: [
      {
        key: 'apiKey',
        kind: 'secret',
        inputType: 'password',
        required: true,
        maxLength: 255,
        format: 'linear_api_key',
        labelKey: 'fields.apiKey.label',
        placeholderKey: 'fields.apiKey.placeholder',
      },
    ],
  },
  stripe: {
    id: 'stripe',
    helpUrl: 'https://dashboard.stripe.com/apikeys',
    recommendedCapabilities: ['read'],
    hasProof: false,
    fields: [
      {
        key: 'token',
        kind: 'secret',
        inputType: 'password',
        required: true,
        maxLength: 255,
        format: 'stripe_secret_or_restricted',
        labelKey: 'fields.token.label',
        placeholderKey: 'fields.token.placeholder',
      },
    ],
  },
  datadog: {
    id: 'datadog',
    helpUrl: 'https://docs.datadoghq.com/account_management/api-app-keys/',
    hasProof: true,
    fields: [
      {
        key: 'apiKey',
        kind: 'secret',
        inputType: 'password',
        required: true,
        maxLength: 64,
        format: 'datadog_api_key',
        labelKey: 'fields.apiKey.label',
        placeholderKey: 'fields.apiKey.placeholder',
      },
      {
        key: 'appKey',
        kind: 'secret',
        inputType: 'password',
        required: true,
        maxLength: 64,
        format: 'datadog_app_key',
        labelKey: 'fields.appKey.label',
        placeholderKey: 'fields.appKey.placeholder',
      },
      {
        key: 'site',
        kind: 'setting',
        inputType: 'select',
        required: true,
        maxLength: 32,
        defaultValue: DEFAULT_DATADOG_SITE,
        options: DATADOG_SITES,
        format: 'none',
        labelKey: 'fields.site.label',
        placeholderKey: 'fields.site.placeholder',
      },
    ],
  },
  codecov: {
    id: 'codecov',
    helpUrl: 'https://app.codecov.io/account/gh/access',
    recommendedCapabilities: ['read'],
    hasProof: false,
    fields: [
      {
        key: 'token',
        kind: 'secret',
        inputType: 'password',
        required: true,
        maxLength: 255,
        format: 'none',
        labelKey: 'fields.token.label',
        placeholderKey: 'fields.token.placeholder',
      },
      {
        key: 'owner',
        kind: 'setting',
        inputType: 'text',
        required: true,
        maxLength: 39,
        format: 'github_owner',
        labelKey: 'fields.owner.label',
        placeholderKey: 'fields.owner.placeholder',
      },
    ],
  },
};

/** Provider ids in declaration order (for iterating the account menu). */
export const PROVIDER_IDS = Object.keys(PROVIDERS) as readonly ProviderId[];

/**
 * Build the POST-body schema for a provider from its field descriptors: each
 * field is trimmed, length-capped, required-checked, constrained to `options`
 * (selects), and format-checked. A failure surfaces as `invalid_format`.
 */
export function credentialInputSchema(
  provider: ProviderId,
): z.ZodType<Record<string, string>> {
  const shape: Record<string, z.ZodTypeAny> = {};
  // eslint-disable-next-line security/detect-object-injection -- provider is a closed-enum key
  for (const field of PROVIDERS[provider].fields) {
    const base = z.string().trim().max(field.maxLength);
    shape[field.key] = (field.required ? base.min(1) : base).refine(
      (value) => {
        if (value === '') return !field.required;
        if (field.options && !field.options.includes(value)) return false;
        if (field.format && !matchesFormat(field.format, value)) return false;
        return true;
      },
      { message: 'invalid_format' },
    );
  }
  return z.object(shape) as z.ZodType<Record<string, string>>;
}
